import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { usePrice } from "@hooks/use-prices";
import { useReloadUser } from "@hooks/use-modify-user";
import { useFormatDate } from "@hooks/use-format-date";
import useCustomizableCloset, { useReloadCustomizableCloset } from "@hooks/use-customizable-closet";
import Button from "@components/core/Button";
import Image from "@components/core/Image";
import { getUnlockEarlyInformations, postUnlockEarly } from "@utils/call-api/users-call";
import { showSuccessToast, showErrorToast } from "@utils/toasts/showToasts";
import DefaultPaymentMethod from "@components/views/Profile/PaymentMethod/DefaultPaymentMethod";
import CustomLoader from "@components/core/CustomLoader";

function UnlockEarlyValidation() {
  const t = useTranslations("common");

  const currentCloset = useCustomizableCloset();
  const price = usePrice();
  const reloadUser = useReloadUser();
  const reloadCurrentCloset = useReloadCustomizableCloset();
  const formatDate = useFormatDate();

  const [unlockEarlyInfo, setUnlockEarlyInfo] = useState({});
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const nextInvoiceDateAfterUnlockEarly = new Date(today.setMonth(today.getMonth() + 1));

  const trackingName = () => {
    switch (currentCloset.state) {
      case "received":
        return "drawerReceived_UnlockEarlyNow";
      case "post_returned":
        return "drawerPostReturned_UnlockEarlyNow";
      case "returned":
        return "drawerReturned_UnlockEarlyNow";
      case "lost":
        return "drawerLost_UnlockEarlyNow";
      case "purchased":
        return "drawerPurchased_UnlockEarlyNow";
      default:
        return "_UnlockEarlyNow";
    }
  };

  async function unlockEarly() {
    try {
      setLoading(true);
      await postUnlockEarly();
      reloadUser();
      reloadCurrentCloset();

      showSuccessToast(
        t("drawer.modal_unlock_early.validation.unlock_early_success.title"),
        t("drawer.modal_unlock_early.validation.unlock_early_success.subtitle"),
      );
    } catch (err) {
      if (err.response?.status === 422) {
        showErrorToast(
          t("drawer.modal_unlock_early.validation.unlock_early_error.title.payment"),
          t("drawer.modal_unlock_early.validation.unlock_early_error.subtitle"),
        );
      } else {
        showErrorToast(
          t("drawer.modal_unlock_early.validation.unlock_early_error.title.unknown"),
          t("drawer.modal_unlock_early.validation.unlock_early_error.subtitle"),
        );
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function initializeUnlockEarlyInfo() {
      const res = await getUnlockEarlyInformations();
      setUnlockEarlyInfo(res);
      setInitializing(false);
    }

    initializeUnlockEarlyInfo();
  }, []);

  return [
    <div
      key="drawer-unlock-early-validation-body"
      className="drawer-body"
    >
      <div className="d-flex flex-column gap-2">
        <div className="p-3 bg-white">
          <div className="px-3 d-flex flex-column align-items-center">
            <h5 className="text-center mb-2">
              {t("drawer.modal_unlock_early.validation.title")}
            </h5>
            <p className="text-center mb-4 text-gray">
              {t("drawer.modal_unlock_early.validation.subtitle")}
            </p>

            <div style={{ width: "80px" }}>
              <Image
                src="handbagHappyRetro"
                alt={t("drawer.modal_unlock_early.validation.alt_happy_bag")}
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="p-3 bg-white">
          <p className="font-weight-bold text-uppercase mb-2 font-size-sm">
            {t("drawer.modal_unlock_early.validation.amount_title")}
          </p>
          {initializing ? (
            <CustomLoader
              variant="SpinnerMiddleGray"
              className="ms-0"
              style={{ width: "1rem" }}
            />
          ) : (
            <p className="mb-0">
              {`${price(unlockEarlyInfo.amount_inc_tax)} ${unlockEarlyInfo.coupon_public_name ? `(${unlockEarlyInfo.coupon_public_name})` : ""}`}
            </p>
          )}
          {unlockEarlyInfo.used_subscription_credit > 0 && (
            <p className="mt-2 mb-0">
              {t("drawer.modal_unlock_early.validation.amount_from_gift_card", {
                amount: price(unlockEarlyInfo.used_subscription_credit),
              })}
            </p>
          )}
        </div>

        <div className="p-3 bg-white">
          <p className="font-weight-bold text-uppercase mb-2 font-size-sm">
            {t("drawer.modal_unlock_early.validation.next_invoice_date_title")}
          </p>
          <p className="mb-0">{formatDate(nextInvoiceDateAfterUnlockEarly)}</p>
        </div>

        <div className="p-3 bg-white">
          <p className="font-weight-bold text-uppercase mb-2 font-size-sm">
            {t("drawer.modal_unlock_early.validation.payment_method_title")}
          </p>

          <DefaultPaymentMethod />
        </div>
      </div>
    </div>,
    <div
      key="drawer-unlock-early-validation-footer"
      className="bg-white p-3 border-top d-flex justify-content-center"
    >
      <Button
        onClick={unlockEarly}
        fullWidth
        loading={loading}
        disabled={initializing}
        trackingName={trackingName()}
      >
        {t("drawer.modal_unlock_early.validation.unlock_early_now")}
      </Button>
    </div>,
  ];
}

export default UnlockEarlyValidation;
